// Utilitários para manipulação de datas e timezone de São Paulo

/**
 * Converte uma data UTC do banco para o horário de São Paulo
 * @param utcDateString - Data em formato ISO string vinda do banco
 * @returns Date object ajustado para São Paulo
 */
export const convertUtcToSaoPaulo = (utcDateString: string): Date => {
  const utcDate = new Date(utcDateString);
  
  // Extrair componentes UTC
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth();
  const day = utcDate.getUTCDate();
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes();
  
  // São Paulo é UTC-3, então subtraímos 3 horas do horário UTC
  const saoPauloHour = hour - 3;
  
  // Criar date com horário de São Paulo (usando construtor local)
  const saoPauloDate = new Date(year, month, day, saoPauloHour, minute, 0, 0);
  
  return saoPauloDate;
};

/**
 * Converte uma data local (São Paulo) para UTC para salvar no banco
 * @param localDateString - Data em formato datetime-local do form (ex: "2025-08-06T20:00")
 * @returns ISO string ajustado para UTC
 */
export const convertSaoPauloToUtc = (localDateString: string): string => {
  // Parse da string datetime-local
  const [datePart, timePart] = localDateString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  
  // Criar data UTC diretamente com os valores de São Paulo + 3 horas
  // Se em São Paulo é 20:00, em UTC deve ser 23:00
  const utcHour = hour + 3;
  const utcDate = new Date(Date.UTC(year, month - 1, day, utcHour, minute, 0, 0));
  
  return utcDate.toISOString();
};

/**
 * Formata uma data para exibição no formato brasileiro
 * @param dateString - Data em formato ISO string
 * @returns String formatada (DD/MM/AAAA)
 */
export const formatDateBR = (dateString: string): string => {
  const date = convertUtcToSaoPaulo(dateString);
  return date.toLocaleDateString('pt-BR');
};

/**
 * Formata uma hora para exibição no formato brasileiro
 * @param dateString - Data em formato ISO string
 * @returns String formatada (HH:MM)
 */
export const formatTimeBR = (dateString: string): string => {
  const date = convertUtcToSaoPaulo(dateString);
  return date.toLocaleTimeString('pt-BR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Formata data e hora completas para exibição
 * @param dateString - Data em formato ISO string
 * @returns String formatada (DD/MM/AAAA às HH:MM)
 */
export const formatDateTimeBR = (dateString: string): string => {
  const date = convertUtcToSaoPaulo(dateString);
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })}`;
};

/**
 * Converte data UTC para formato datetime-local (para forms)
 * @param utcDateString - Data em formato ISO string vinda do banco
 * @returns String no formato YYYY-MM-DDTHH:MM
 */
export const convertUtcToDateTimeLocal = (utcDateString: string): string => {
  const utcDate = new Date(utcDateString);
  
  // Extrair componentes UTC
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth() + 1; // +1 porque getUTCMonth() retorna 0-11
  const day = utcDate.getUTCDate();
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes();
  
  // São Paulo é UTC-3, então subtraímos 3 horas do horário UTC
  const saoPauloHour = hour - 3;
  
  // Formatar como datetime-local
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  const hourStr = String(saoPauloHour).padStart(2, '0');
  const minuteStr = String(minute).padStart(2, '0');
  
  return `${year}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}`;
};

/**
 * Função de debug para testar conversões de timezone
 */
export const debugTimezoneFunctions = (): void => {
  console.log('🧪 === TESTE DE CONVERSÕES DE TIMEZONE (CORRIGIDO) ===');
  
  // Teste 1: Converter 20:00 São Paulo para UTC
  const sampleLocalTime = '2025-08-06T20:00';
  console.log('📝 Input: São Paulo 20:00 ->', sampleLocalTime);
  
  const utcResult = convertSaoPauloToUtc(sampleLocalTime);
  console.log('📝 Convertido para UTC ->', utcResult);
  console.log('📝 Esperado: 2025-08-06T23:00:00.000Z');
  console.log('📝 ✅ Salvamento correto?', utcResult === '2025-08-06T23:00:00.000Z' ? 'SIM!' : 'NÃO!');
  
  // Teste 2: Converter de volta para São Paulo (Date object)
  const backToSP = convertUtcToSaoPaulo(utcResult);
  console.log('📝 De volta para São Paulo (Date) ->', backToSP);
  console.log('📝 Hora extraída:', backToSP.getHours() + ':' + String(backToSP.getMinutes()).padStart(2, '0'));
  console.log('📝 ✅ Deve ser 20:00?', backToSP.getHours() === 20 ? 'SIM!' : 'NÃO!');
  
  // Teste 3: Para formato datetime-local
  const dateTimeLocal = convertUtcToDateTimeLocal(utcResult);
  console.log('📝 Para datetime-local ->', dateTimeLocal);
  console.log('📝 Esperado: 2025-08-06T20:00');
  console.log('📝 ✅ Exibição correta?', dateTimeLocal === '2025-08-06T20:00' ? 'SIM!' : 'NÃO!');
  
  // Teste 4: Formatação brasileira
  console.log('📝 Formato BR completo ->', formatDateTimeBR(utcResult));
  console.log('📝 Apenas hora BR ->', formatTimeBR(utcResult));
  
  // Teste 5: Outros horários
  console.log('🧪 === TESTANDO OUTROS HORÁRIOS ===');
  
  const teste14h = convertSaoPauloToUtc('2025-08-06T14:00');
  console.log('📝 14:00 SP -> UTC:', teste14h, '(esperado: 17:00 UTC)');
  const back14h = convertUtcToDateTimeLocal(teste14h);
  console.log('📝 14:00 UTC -> SP:', back14h, '(deve voltar: 14:00)');
  
  const teste08h = convertSaoPauloToUtc('2025-08-06T08:00');
  console.log('📝 08:00 SP -> UTC:', teste08h, '(esperado: 11:00 UTC)');
  const back08h = convertUtcToDateTimeLocal(teste08h);
  console.log('📝 08:00 UTC -> SP:', back08h, '(deve voltar: 08:00)');
  
  console.log('🧪 === FIM DOS TESTES ===');
};